import React, { FC, useState } from 'react'
import {
  Box,
  Chip,
  Divider,
  Fab,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import AddIcon from '@mui/icons-material/Add'
import {
  MaterialType,
  MaterialGroupType,
  HardwareType,
} from '../../../api/appAPI'
import { Flex } from '../../common/Helpers'

type TextfieldProps = {
  value: string
  onChange(event: React.ChangeEvent<{ value: string }>): void
}

type TagsProps = {
  value: string[]
  onChange(value: string[]): void
}

type TypeProps = {
  value: 'hardware' | 'material' | 'materialGroup'
  onChange(event: React.ChangeEvent<{ value: string }>): void
}

type TargetIdProps = {
  value: number
  onChange(event: React.ChangeEvent<{ value: unknown }>): void
}

interface IProps {
  title: string
  name: TextfieldProps
  description: TextfieldProps
  type: TypeProps
  tags: TagsProps
  targetId: TargetIdProps

  materials: MaterialType[]
  hardwares: HardwareType[]
  materialGroups: MaterialGroupType[]

  onButtonClick(): void
  needSave?: boolean
}

const suggestedTags = ['GC', 'GSH', 'OS']

const TagsInput: FC<TagsProps> = (props) => {
  const [inputValue, setInputValue] = useState('')

  const tagsArray = props.value || []

  const handleAdd = (tagToAdd?: string) => {
    const value = tagToAdd || inputValue.trim()
    if (value && !tagsArray.includes(value)) {
      const newTags = [...tagsArray, value]
      props.onChange(newTags)
      if (!tagToAdd) setInputValue('')
    }
  }

  const handleDelete = (tagToDelete: string) => {
    const newTags = tagsArray.filter((t) => t !== tagToDelete)
    props.onChange(newTags)
  }

  return (
    <>
      <Divider style={{ width: '80%', marginTop: 15, marginBottom: 5 }} />
      <Flex>
        <Typography>Tags</Typography>
      </Flex>
      <Box
        component="ul"
        px={1}
        my={1}
        style={{
          display: 'flex',
          justifyContent: 'center',
          columnGap: 10,
          rowGap: 10,
          flexWrap: 'wrap',
          listStyle: 'none',
          padding: 0,
        }}
      >
        {tagsArray.map((tag, idx) => (
          <li key={idx}>
            <Chip label={tag} onDelete={() => handleDelete(tag)} />
          </li>
        ))}
      </Box>

      <TextField
        label="Add Tag"
        variant="outlined"
        size="small"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            handleAdd()
          }
        }}
        onBlur={() => handleAdd()}
      />

      <Box
        display="flex"
        alignItems="center"
        mt={2}
        flexWrap="wrap"
        gap={1}
        justifyContent="center"
      >
        <Typography variant="body2" color="textSecondary">
          Suggested:
        </Typography>
        {suggestedTags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            onClick={() => handleAdd(tag)}
            disabled={tagsArray.includes(tag)}
          />
        ))}
      </Box>
    </>
  )
}

const DtoolRelationUIBox: FC<IProps> = (props) => {
  const getTargetOptions = () => {
    switch (props.type.value) {
      case 'hardware':
        return props.hardwares.map((h) => ({ id: h.id, name: h.name }))
      case 'material':
        return props.materials.map((m) => ({ id: m.id, name: m.name }))
      case 'materialGroup':
        return props.materialGroups.map((mg) => ({ id: mg.id, name: mg.name }))
      default:
        return []
    }
  }

  const options = getTargetOptions()

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      pb={2}
      style={{
        width: 400,
        height: 'fit-content',
        margin: 15,
        border: '1px solid black',
        maxHeight: '90%',
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'relative',
      }}
    >
      <Box
        py={1}
        style={{
          position: 'sticky',
          top: 0,
          width: '100%',
          backgroundColor: 'white',
          zIndex: 10,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={3}>
            {' '}
          </Grid>
          <Grid
            item
            xs={6}
            container
            justifyContent="center"
            alignItems="center"
          >
            <Typography variant="h6">{props.title}</Typography>
          </Grid>
          <Grid
            item
            xs={3}
            container
            justifyContent="flex-end"
            alignItems="center"
          >
            <Tooltip title={props.title.startsWith('New') ? 'Create' : 'Save'}>
              <Fab
                color={props.needSave ? 'secondary' : 'primary'}
                size="small"
                onClick={props.onButtonClick}
                style={{ marginRight: 10 }}
              >
                {props.title.startsWith('New') ? <NoteAddIcon /> : <SaveIcon />}
              </Fab>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      <TextField
        label="Name"
        variant="outlined"
        value={props.name.value}
        onChange={props.name.onChange}
        style={{ width: '60%', marginTop: 15 }}
      />

      <TextField
        label="Description"
        variant="outlined"
        multiline
        minRows={3}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
          }
        }}
        value={props.description.value}
        onChange={props.description.onChange}
        style={{ width: '60%', marginTop: 15 }}
      />

      <FormControl variant="outlined" style={{ width: '60%', marginTop: 15 }}>
        <InputLabel>Type</InputLabel>
        <Select
          value={props.type.value}
          onChange={props.type.onChange as any}
          label="Type"
        >
          <MenuItem value="hardware">Hardware</MenuItem>
          <MenuItem value="material">Material</MenuItem>
          <MenuItem value="materialGroup">Material Group</MenuItem>
        </Select>
      </FormControl>

      <FormControl variant="outlined" style={{ width: '60%', marginTop: 15 }}>
        <InputLabel>Select Target</InputLabel>
        <Select
          value={props.targetId.value}
          onChange={props.targetId.onChange as any}
          label="Select Target"
        >
          {options.map((opt) => (
            <MenuItem key={opt.id} value={opt.id}>
              {opt.id} - {opt.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TagsInput value={props.tags.value} onChange={props.tags.onChange} />
    </Box>
  )
}

export default DtoolRelationUIBox
